import React from 'react';
import type { MaterialDoc } from '../shared/contract';
import type { InterviewSetup } from './types';

/** Ideals content is served by the authenticated `GET /api/content`; it is never bundled into client JS. */
export interface TaniaProfile {
  name: string;
  title: string;
  company: string;
  background: string;
  interviewFocus: string;
  likelyQuestions: string[];
}

export interface CandidateProfileContent {
  label: string;
  summary: string;
  cv: string;
}

export type SourceKind = 'official' | 'public' | 'unverified';

export interface PrepSource {
  label: string;
  url: string;
  kind: SourceKind;
}

export interface PrepCard {
  id: string;
  topic: string;
  title: string;
  summary: string;
  /** 3 = must know, 2 = good to know, 1 = background. */
  importance: 1 | 2 | 3;
  tags: string[];
  markdown: string;
  sources: PrepSource[];
}

export interface RubricDimension {
  id: string;
  dimension: string;
  whatExcellentLooksLike: string;
  evidenceToLookFor: string[];
  /** Level 1 (not shown) to 5 (exemplary). */
  anchors: Array<{ level: number; text: string }>;
  sources: PrepSource[];
  references: string[];
}

export interface CassieProfile {
  name: string;
  title: string;
  company: string;
  linkedinUrl: string;
  summary: string;
  strengths: string[];
  gaps: string[];
}

export interface ContentBundle {
  cassieProfile: CassieProfile | null;
  evaluationRubric: RubricDimension[];
  prepLibrary: PrepCard[];
  defaultMaterials: MaterialDoc[];
  candidateProfiles: Record<string, CandidateProfileContent>;
  prepPack: Array<{ id: string; title: string; markdown: string; sources: PrepSource[] }>;
  references: PrepReference[];
  taniaProfile: TaniaProfile | null;
}

export type RefKind = 'official' | 'ideals_public' | 'third_party' | 'linkedin' | 'other';

export interface PrepReference {
  id: string;
  title: string;
  url: string;
  kind: RefKind;
  note: string;
}

export const REF_KIND_LABELS: Record<RefKind, string> = {
  official: 'Official',
  ideals_public: 'Ideals public',
  third_party: 'Third-party',
  linkedin: 'LinkedIn',
  other: 'Other',
};

function refKind(value: unknown): RefKind {
  const text = String(value ?? '').toLowerCase().replace(/[\s-]+/gu, '_');
  if (text.includes('linkedin')) return 'linkedin';
  if (text.includes('ideals') || text.includes('company')) return 'ideals_public';
  if (text.startsWith('official')) return 'official';
  if (text.includes('third') || text.startsWith('public') || text.includes('press') || text.includes('news')) return 'third_party';
  return 'other';
}

function badgeFor(kind: RefKind): SourceKind {
  if (kind === 'official') return 'official';
  if (kind === 'ideals_public' || kind === 'third_party') return 'public';
  return 'unverified';
}

function safeUrl(value: unknown): string {
  return typeof value === 'string' && /^https?:\/\//u.test(value) ? value : '';
}

export function normalizeReferences(raw: unknown): PrepReference[] {
  if (!Array.isArray(raw)) return [];
  const out: PrepReference[] = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const ref = item as Record<string, unknown>;
    const url = safeUrl(ref.url ?? ref.href);
    const title = String(ref.title ?? ref.label ?? ref.name ?? url);
    if (!title) return;
    out.push({
      id: String(ref.id ?? ref.n ?? index + 1),
      title,
      url,
      kind: refKind(ref.kind ?? ref.type ?? ref.group),
      note: [ref.publisher, ref.date, ref.note ?? ref.description].filter((part) => typeof part === 'string' && part).join(' · '),
    });
  });
  return out;
}

/** Sources can be inline objects or ids pointing at the global reference list. */
function resolveSources(raw: unknown, refs: ReadonlyMap<string, PrepReference>): PrepSource[] {
  if (!Array.isArray(raw)) return [];
  const sources: PrepSource[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string' || typeof entry === 'number') {
      const ref = refs.get(String(entry));
      if (ref) sources.push({ label: ref.title, url: ref.url, kind: badgeFor(ref.kind) });
      continue;
    }
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    const linked = item.ref !== undefined ? refs.get(String(item.ref)) : item.id !== undefined && !item.url ? refs.get(String(item.id)) : undefined;
    const url = safeUrl(item.url) || linked?.url || '';
    const label = String(item.label ?? item.title ?? item.name ?? linked?.title ?? url);
    if (!url && !label) continue;
    const kindSource = item.kind ?? item.type ?? item.trust ?? item.reliability;
    const kind: SourceKind =
      kindSource === undefined && linked
        ? badgeFor(linked.kind)
        : ['official', 'public', 'unverified'].includes(String(kindSource).toLowerCase())
          ? (String(kindSource).toLowerCase() as SourceKind)
          : badgeFor(refKind(kindSource));
    sources.push({ label, url, kind });
  }
  return sources;
}

/** Lenient: a missing or partly filled library must never crash the page. */
export function normalizePrepLibrary(raw: unknown, refs: ReadonlyMap<string, PrepReference> = new Map()): PrepCard[] {
  if (!Array.isArray(raw)) return [];
  const cards: PrepCard[] = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const card = item as Record<string, unknown>;
    if (typeof card.title !== 'string' || !card.title.trim()) return;
    const importance = Number(card.importance);
    cards.push({
      id: typeof card.id === 'string' ? card.id : `card-${index}`,
      topic: String(card.topic ?? card.category ?? 'Other'),
      title: card.title,
      summary: String(card.summary ?? ''),
      importance: importance >= 3 ? 3 : importance === 2 ? 2 : 1,
      tags: Array.isArray(card.tags) ? card.tags.map(String) : [],
      markdown: String(card.markdown ?? ''),
      sources: resolveSources(card.sources ?? card.references ?? card.refs, refs),
    });
  });
  return cards;
}

export function normalizeRubric(raw: unknown, refs: ReadonlyMap<string, PrepReference>): RubricDimension[] {
  if (!Array.isArray(raw)) return [];
  const out: RubricDimension[] = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const row = item as Record<string, unknown>;
    if (typeof row.dimension !== 'string') return;
    const anchorSource = row.anchors && typeof row.anchors === 'object' ? (row.anchors as Record<string, unknown>) : {};
    out.push({
      id: String(row.id ?? `dimension-${index}`),
      dimension: row.dimension,
      whatExcellentLooksLike: String(row.whatExcellentLooksLike ?? ''),
      evidenceToLookFor: Array.isArray(row.evidenceToLookFor) ? row.evidenceToLookFor.map(String) : [],
      anchors: [1, 2, 3, 4, 5]
        .filter((level) => typeof anchorSource[level] === 'string')
        .map((level) => ({ level, text: String(anchorSource[level]) })),
      sources: resolveSources(row.sources, refs),
      references: Array.isArray(row.references) ? row.references.map((ref) => (typeof ref === 'string' ? ref : String((ref as { label?: unknown })?.label ?? ''))).filter(Boolean) : [],
    });
  });
  return out;
}

function textList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') return value.split('\n').map((line) => line.replace(/^\s*[-*•]\s*/u, '').trim()).filter(Boolean);
  return [];
}

export function normalizeCassie(raw: unknown): CassieProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.name !== 'string') return null;
  return {
    name: item.name,
    title: String(item.title ?? item.headline ?? ''),
    company: String(item.company ?? item.employer ?? ''),
    linkedinUrl: safeUrl(item.linkedinUrl ?? item.linkedin ?? item.url),
    summary: String(item.summary ?? item.background ?? ''),
    strengths: textList(item.strengths),
    gaps: textList(item.gaps ?? item.gapsToAddress ?? item.weaknesses),
  };
}

/** Plain-text version for the materials list. */
export function cassieBackgroundText(profile: CassieProfile): string {
  return [
    `${profile.name} — ${profile.title}${profile.company ? `, ${profile.company}` : ''}`,
    profile.summary,
    profile.strengths.length ? `Strengths:\n${profile.strengths.map((item) => `- ${item}`).join('\n')}` : '',
    profile.gaps.length ? `Gaps to address:\n${profile.gaps.map((item) => `- ${item}`).join('\n')}` : '',
  ].filter(Boolean).join('\n\n');
}

export const ContentContext = React.createContext<ContentBundle | null>(null);

export function useContent(): ContentBundle | null {
  return React.useContext(ContentContext);
}

/** Normalises the API payload so a partially filled response cannot crash the UI. */
export function normalizeContent(raw: unknown): ContentBundle {
  const data = (raw ?? {}) as Record<string, unknown>;
  const references = normalizeReferences(data.references);
  const refMap = new Map(references.map((ref) => [ref.id, ref]));
  const tania = data.taniaProfile as Partial<TaniaProfile> | null | undefined;
  return {
    cassieProfile: normalizeCassie(data.cassieProfile),
    evaluationRubric: normalizeRubric(data.evaluationRubric, refMap),
    prepLibrary: normalizePrepLibrary(data.prepLibrary, refMap),
    defaultMaterials: Array.isArray(data.defaultMaterials) ? (data.defaultMaterials as MaterialDoc[]) : [],
    candidateProfiles:
      data.candidateProfiles && typeof data.candidateProfiles === 'object'
        ? (data.candidateProfiles as Record<string, CandidateProfileContent>)
        : {},
    references,
    prepPack: (Array.isArray(data.prepPack) ? data.prepPack : [])
      .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
      .map((item, index) => ({
        id: String(item.id ?? `section-${index}`),
        title: String(item.title ?? ''),
        markdown: String(item.markdown ?? ''),
        sources: resolveSources(item.sources ?? item.references ?? item.refs, refMap),
      })),
    taniaProfile:
      tania && typeof tania.name === 'string'
        ? {
            name: tania.name,
            title: String(tania.title ?? ''),
            company: String(tania.company ?? ''),
            background: String(tania.background ?? ''),
            interviewFocus: String(tania.interviewFocus ?? ''),
            likelyQuestions: Array.isArray(tania.likelyQuestions) ? tania.likelyQuestions.map(String) : [],
          }
        : null,
  };
}

/** The CV the recruiter would hold in the real call. The bundle never contains hidden truths. */
export function candidateCvFor(
  setup: InterviewSetup,
  content: ContentBundle | null,
): { label: string; text: string } | null {
  if (setup.scenario !== 'roleplay_screening') return null;
  if (setup.candidateProfile === 'from_materials') {
    const doc = setup.materials.find((material) => material.kind === 'candidate_cv');
    return doc ? { label: doc.name, text: doc.text } : null;
  }
  const profile = content?.candidateProfiles[setup.candidateProfile];
  return profile ? { label: profile.label, text: profile.cv } : null;
}

export function profileSummary(content: ContentBundle | null, profileId: string): string {
  return content?.candidateProfiles[profileId]?.summary ?? '';
}

/** Built-in profiles have hidden truths; uploaded-CV candidates do not. */
export function hasHiddenTruths(setup: InterviewSetup): boolean {
  return setup.scenario === 'roleplay_screening' && setup.candidateProfile !== 'from_materials';
}
